import { ColorStatus } from '../types';

/**
 * Single source of truth for the green/yellow/red status palette.
 * Chart components import these; CSS uses the custom properties set by
 * applyStatusColorCssVars() in src/index.tsx.
 */
export const STATUS_COLORS: Record<ColorStatus, string> = {
  green: '#10b981',
  yellow: '#f59e0b',
  red: '#ef4444',
  blue: '#3b82f6',
  teal: '#14b8a6',
  amber: '#f97316',
  gray: '#6b7280',
};

/** Darker partner shades for gradients (MetricCard accent bars). */
export const STATUS_COLORS_DARK: Record<ColorStatus, string> = {
  green: '#059669',
  yellow: '#d97706',
  red: '#dc2626',
  blue: '#2563eb',
  teal: '#0d9488',
  amber: '#ea580c',
  gray: '#4b5563',
};

/**
 * Text-safe shades for status text/labels shown on the light status tint
 * backgrounds (e.g. DataCard, comparison indicators, trend summaries).
 * STATUS_COLORS_DARK fails AA (< 4.5:1) against those tints; these pass
 * against both tint shades used across the app (e.g. #ecfdf5/#d1fae5).
 */
export const STATUS_COLORS_TEXT: Record<ColorStatus, string> = {
  green: '#047857',
  yellow: '#92400e',
  red: '#991b1b',
  blue: '#1d4ed8',
  teal: '#0f766e',
  amber: '#9a3412',
  gray: '#374151',
};

/**
 * The comparison pill style for a status. Only green is "above"; the calm band
 * colors (blue for "Typical for your age", teal, gray) must not look like a
 * warning, so they get their own "typical" style; the warm colors are "below".
 */
export function comparisonIndicatorVariant(status: ColorStatus): 'above' | 'typical' | 'below' {
  switch (status) {
    case 'green': return 'above';
    case 'blue':
    case 'teal':
    case 'gray': return 'typical';
    default: return 'below';
  }
}

/**
 * Plain-English fallback words, so color is never the only signal for a status
 * (WCAG 1.4.1). Used only when a card has no label of its own.
 */
export const STATUS_FALLBACK_LABELS: Record<ColorStatus, string> = {
  green: 'In range',
  yellow: 'Close to range',
  red: 'Needs attention',
  blue: 'In range',
  teal: 'In range',
  amber: 'Close to range',
  gray: 'No status',
};

/**
 * The status label a metric card hands to its chart.
 *
 * A label that was actually set (an admin threshold label, or a norms band
 * label) always wins. Otherwise the fallback word is used - EXCEPT in
 * single-color mode. There, color is not a status signal, and the admin screen
 * promises the saved threshold ranges are not used, so a word derived from them
 * would be false. Returning undefined lets each chart fall back to its own
 * single-color text instead ("Tracking", or a comparison with the target).
 */
export function resolveCardStatusLabel(
  statusLabel: string | undefined,
  colorStatus: ColorStatus,
  customColor: string | undefined
): string | undefined {
  if (statusLabel) return statusLabel;
  if (customColor) return undefined;
  return STATUS_FALLBACK_LABELS[colorStatus];
}

export function getStatusColor(status: ColorStatus): string {
  return STATUS_COLORS[status] ?? STATUS_COLORS.green;
}

export function applyStatusColorCssVars(root: HTMLElement = document.documentElement): void {
  (Object.keys(STATUS_COLORS) as ColorStatus[]).forEach((s) => {
    root.style.setProperty(`--status-${s}`, STATUS_COLORS[s]);
    root.style.setProperty(`--status-${s}-dark`, STATUS_COLORS_DARK[s]);
    root.style.setProperty(`--status-${s}-text`, STATUS_COLORS_TEXT[s]);
  });
}
